// How the tracking trees show amounts: compact in narrow columns, exact
// in tooltips, always with the project's unit.
export const DEFAULT_UNITS = { length: "ft", area: "ft²", volume: "ft³", weight: "lb" };

// The additive amounts a node rolls up, in the order the columns show them.
export const SUM_COLUMNS = [
  { key: "length", label: "Length", unit: "length" },
  { key: "area", label: "Area", unit: "area" },
  { key: "volume", label: "Volume", unit: "volume" },
];
export const DETAIL_SUMS = [
  { key: "count", label: "Count" },
  { key: "quantity", label: "Qty" },
  { key: "length", label: "Length", unit: "length" },
  { key: "area", label: "Area", unit: "area" },
  { key: "surfaceArea", label: "Surface area", unit: "area" },
  { key: "volume", label: "Volume", unit: "volume" },
  { key: "weight", label: "Weight", unit: "weight" },
];
// THE COLUMNS THE DETAILED TABLE SHOWS, which is not the same list.
//
// WEIGHT IS NOT ONE OF THEM (owner, 2026-10-03: "drop the weight column then
// from quantities table"). It is not that the number is wrong - there is no
// number. Measured on the live BA03 commit before removing it: 0 of 339,354
// rows carry a weight, so every cell is a dash and every roll-up is a dash.
// Three independent reasons, each sufficient:
//
//   1. The desktop plugin has NO WeightInfoReader at all (Area, Connector,
//      Depth, Length, Material, NominalWeight, Volume, Width - no Weight),
//      so a per-element mass is never exported.
//   2. What IS exported is NominalWeight in POUNDS PER FOOT - a force per
//      length, read from structural section parameters. utils/recordQuantities
//      deliberately refuses to map it into our mass kind, and says why: doing
//      so would read pounds-per-foot AS pounds, the same class of error as the
//      force/mass lookup behind the 16M-to-2.1M correction. A real mass needs
//      a new kind multiplied by each element's length.
//   3. On this project there is nothing to multiply anyway: all 6,561
//      Structural Framing and 3,416 Columns are Direct Shapes named BEAM,
//      HSS-BRACE, Z-GIRT - imported geometry with no section type, so no
//      nominal weight exists on them either.
//
// IT STAYS IN DETAIL_SUMS, on purpose. That list is the VOCABULARY - it gives
// labels to measures the server returns (SelectionDetails) and the choices in
// the rule builder - and dropping it there would make an existing saved rule
// on weight unrenderable and print a measure as the bare word "weight".
// Removing a COLUMN is a presentation decision; removing a measure from the
// vocabulary is not, and these are different lists for that reason.
//
// ADD IT BACK THE DAY AN EXPORT CARRIES A MASS, and say so here.
export const TABLE_SUMS = DETAIL_SUMS.filter((c) => c.key !== "weight");

// Per-element dimensions: shown on elements, never summed.
export const DETAIL_DIMENSIONS = [
  { key: "width", label: "Width", unit: "length" },
  { key: "depth", label: "Depth", unit: "length" },
  { key: "height", label: "Height", unit: "length" },
  { key: "diameter", label: "Diameter", unit: "length" },
  { key: "thickness", label: "Thickness", unit: "length" },
];

// 1234567 -> "1.23M", 35 -> "35", 0.6667 -> "0.67", 0 -> "–"
export function compact(v) {
  const n = Number(v) || 0;
  if (!n) return "–";
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k`;
  if (a >= 100) return Math.round(n).toLocaleString();
  return String(Math.round(n * 100) / 100);
}

export function exact(v, unit) {
  const n = Number(v) || 0;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
}

export function unitOf(col, units) {
  const u = units || DEFAULT_UNITS;
  return col.unit ? u[col.unit] || "" : "";
}

// "Reading quantities from the model files: 4 of 17…" for a build that
// is not done yet, or null when there is nothing to say.
export function buildNote(q) {
  if (!q || q.state === "done") return null;
  if (q.state === "none") return "No record files to read quantities from - publish from the plugin.";
  if (q.state === "failed") return `Quantities could not be read: ${q.error || "the build failed"}.`;
  if (q.state === "queued") return "Quantities are queued - they are read from the model files in the background.";
  return `Reading quantities from the model files: ${q.filesDone || 0} of ${q.files || "?"} files, ${(q.components || 0).toLocaleString()} elements so far…`;
}

// HOW CERTAIN A NUMBER IS (owner, 2026-09-29, from Tom's certainty note).
//
// The exporter fills a measure down a ladder - Revit's own measure, the type
// parameter, the instance parameter, then the element's WORLD-AXIS BOUNDING
// BOX. Area falls back to Length x Width and Volume to Length x Width x Depth.
// The last rungs are estimates and for anything rotated or diagonal they
// OVERSTATE: an estimated volume is an upper bound, not a quantity. The
// exporter names them per element in EstimatedFields, and we store them as
// `est`.
//
// THREE STATES, NOT TWO, and the third is the one that matters:
//   reported   the export carries the flag and did not name this measure
//   estimated  the export named it
//   unknown    the export predates the flag (plugin 26.09.27.1) and says
//              NOTHING about any measure
// Reading `unknown` as `reported` would make the oldest data look the most
// trustworthy, which is exactly backwards.
export const CERTAINTY = { REPORTED: "reported", ESTIMATED: "estimated", UNKNOWN: "unknown" };

/**
 * @param {object|null} row     an element row, which may carry `est`
 * @param {string} field        our field name, e.g. "volume"
 * @param {boolean} flagAware   did THIS export carry the flag at all
 */
export function certaintyOf(row, field, flagAware) {
  if (!flagAware) return CERTAINTY.UNKNOWN;
  const est = row && Array.isArray(row.est) ? row.est : [];
  return est.includes(field) ? CERTAINTY.ESTIMATED : CERTAINTY.REPORTED;
}

/** The mark that goes in front of a number. Nothing for a reported one. */
export function certaintyMark(c) {
  if (c === CERTAINTY.ESTIMATED) return "~";
  if (c === CERTAINTY.UNKNOWN) return "?";
  return "";
}

export function certaintyTitle(c, field) {
  const what = field ? `This ${field}` : "This value";
  if (c === CERTAINTY.ESTIMATED) {
    return `${what} is ESTIMATED - the exporter could not read it from the element and fell back to its bounding box (area and volume are multiplied from those). For anything rotated or diagonal it overstates, so treat it as an upper bound.`;
  }
  if (c === CERTAINTY.UNKNOWN) {
    return `${what}'s certainty is UNKNOWN - this model was exported before the plugin recorded which measures it had to estimate. Re-export to find out.`;
  }
  return `${what} is reported by Revit for this element.`;
}

/** HOW MUCH OF THIS TABLE RESTS ON ESTIMATES, in one sentence over the table.
 *
 * Said as a COUNT of elements and not as a percentage of any total: the
 * elements whose volume is a bounding box are usually the big ones, so "2% of
 * elements" and "most of the volume" are both true at once and the percentage
 * would be the reassuring half. Null when there is nothing to warn about.
 */
export function certaintyNote(q) {
  if (!q || q.state !== "done") return null;
  if (!q.flagAware) {
    return "This model was exported before the plugin recorded which measures it had to estimate, so no number here can say whether it was read from the element or derived from its bounding box. Re-export to find out.";
  }
  const n = Number(q.estimatedComponents) || 0;
  if (!n) return null;
  const by = q.estimatedByField || {};
  const worst = Object.entries(by).sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([f, c]) => `${f} on ${Number(c).toLocaleString()}`);
  return `${n.toLocaleString()} element${n === 1 ? " has a measure" : "s have measures"} the exporter estimated from a bounding box, marked ~ below${worst.length ? ` (${worst.join(", ")})` : ""}. Estimated areas and volumes are upper bounds - they overstate for anything rotated or diagonal.`;
}
